/**
 * Хранилище модуля [PageOrders]{@link module:PageOrders}
 * @module PageOrders/Store
 * @requires npm:vue
 * @requires npm:vuex
 * @requires Utils/notify
 * @requires API/orders
 * */

import Vue from 'vue'
import Vuex from 'vuex'
import { notifyError } from 'Utils/notify'
import * as ordersService from 'API/orders'

Vue.use(Vuex)

/**
 * Мутация SET_ORDERS
 * @constant {String}
 * */
const SET_ORDERS = 'SET_ORDERS'

/**
 * Возвращает состояние хранилища по умолчанию
 * @return {Object}
 * */
const defaultState = () => ({
  /**
   * Заказы на доставку
   * @name state/orders
   * @type {Array.<Object>}
   * */
  orders: [],

  /**
   * Всего записей удовлетворяющих требованиям
   * @name state/totalAmount
   * @type {Number}
   * */
  totalAmount: null,

  /**
   * Страница
   * @name state/page
   * @type {Number}
   * */
  page: 1,

  /**
   * Записей на страницу
   * @name state/perPage
   * @type {Number}
   * */
  perPage: 10
})

export default new Vuex.Store({
  state: {
    ...defaultState()
  },
  getters: {
    /**
     * Количество страниц
     * @name getters/totalPages
     * @type {Number}
     * */
    totalPages(state) {
      return Math.ceil(state.totalAmount / state.perPage)
    },

    deserializeOrder: (state, getters) => (
      /**
       * Десериализовать заказ
       * @name getters/deserializeOrder
       * @param {Object} payload
       * */
      (payload) => ({
        id: payload.id,
        orderNumber: payload.order_number,
        orderCreatedAtDate: payload.order_created_at_date,
        orderCreatedAtTime: payload.order_created_at_time,
        orderCost: payload.order_cost,
        orderStatus: payload.order_status,
        orderPaymentStatus: payload.order_payment_status,
        orderDeliveryStatus: payload.order_delivery_status,
        orderCollectorFullName: payload.order_collector_full_name,
        addressFrom: payload.address_from,
        shopNumber: payload.shop_number,
        addressTo: payload.address_to,
        commentFromClient: payload.comment_from_client,
        contactlessDelivery: payload.contactless_delivery,
        customerFullName: payload.customer_full_name,
        courierServiceName: payload.courier_service_name,
        courierFullName: payload.courier_full_name,
        courierPhone: payload.courier_phone,
        orderList: payload.order_list.map(el => getters.deserializeOrderListItem(el))
      })
    ),
    deserializeOrderListItem: () => (
      /**
       * Десериализовать продукт из состава заказа
       * @name getters/deserializeOrderListItem
       * @param {Object} payload
       * */
      (payload) => ({
        vendorCode: payload.vendor_code,
        productName: payload.product_name,
        unitCost: payload.unit_cost,
        checkCost: payload.check_cost,
        orderedQuantity: payload.ordered_quantity,
        selectedOrders: payload.selected_orders,
        notAvailable: payload.not_available,
        replacement: payload.replacement
      })
    )
  },
  actions: {
    /**
     * Получть список заказов
     * @async
     * @param {Object} context
     * @return {Promise<void>}
     * */
    async getOrders({ commit, getters }) {
      try {
        const { data } = await ordersService.getOrders()
        commit(SET_ORDERS, data.map(el => getters.deserializeOrder(el)))
      } catch (err) {
        console.error(err)
        notifyError(err.response?.data?.message)
      }
    }
  },
  mutations: {
    /**
     * Записать в хранилище страницу заказов
     * @function
     * @name mutations/SET_ORDERS
     * @param {Object} state
     * @param {Array.<Object>} payload
     * */
    [SET_ORDERS](state, payload) {
      state.orders = payload
    }
  }
})
